import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, UtensilsCrossed, RefreshCw } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { posAuth } from '../../api/posAPI';
import api from '../../api/axiosConfig';
import Button from '../../components/Button';
import usePosStore from '../../stores/posStore';
import { useShiftSessionStore } from '../../features/pos/stores/shiftSessionStore';

const TERMINAL_KEY = 'ventahub_terminal_id';
const DENOMINATIONS = [1000, 500, 200, 100, 50, 20, 10, 5, 1];

export default function ShiftStart() {
  const navigate = useNavigate();
  const { setPosSession } = usePosStore();
  const {
    terminalId: storedTerminalId,
    sessionStatus,
    activeSession,
    setActiveSession,
  } = useShiftSessionStore();

  const isResume = sessionStatus === 'open' && !!activeSession;

  const [form, setForm] = useState({
    username: '',
    pin: '',
    terminalId: storedTerminalId || '',
  });
  const [denoms, setDenoms] = useState(
    Object.fromEntries(DENOMINATIONS.map((d) => [d, 0]))
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Sync terminalId from store once initTerminalId() resolves
  useEffect(() => {
    if (storedTerminalId && !form.terminalId) {
      setForm((f) => ({ ...f, terminalId: storedTerminalId }));
    }
  }, [storedTerminalId]);

  const openingFloat = isResume
    ? 0
    : DENOMINATIONS.reduce((sum, d) => sum + d * 100 * (denoms[d] || 0), 0);

  // Store-selection step — shown when login fails because no store is selected
  const [storeOptions, setStoreOptions] = useState(null); // null = step hidden
  const [selectedStoreId, setSelectedStoreId] = useState('');

  const openStorePicker = async () => {
    try {
      const { data } = await api.get('/store');
      setStoreOptions((data?.data || []).filter((s) => s.isActive !== false));
    } catch {
      setError('Could not load your stores. Please try again.');
    }
  };

  const handleStoreContinue = () => {
    if (!selectedStoreId) return;
    // Same key the portal's store switcher uses, so x-store-id matches the POS store
    localStorage.setItem('storeId', selectedStoreId);
    setStoreOptions(null);
    submitLogin(selectedStoreId);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    submitLogin(localStorage.getItem('storeId'));
  };

  const submitLogin = async (restaurantId) => {
    setError(null);
    setLoading(true);
    try {
      const terminalId = isResume ? activeSession.terminalId : form.terminalId.trim();

      const denominations = isResume
        ? []
        : DENOMINATIONS.filter((d) => denoms[d] > 0).map((d) => ({
            bill: d * 100,
            count: denoms[d],
          }));

      // Persist any edited terminal ID to localStorage on fresh start
      if (!isResume && terminalId) {
        localStorage.setItem(TERMINAL_KEY, terminalId);
      }

      // Step 1: authenticate the cashier and get POS JWT
      const res = await posAuth.cashierLogin({
        // Staff usernames are unique only per restaurant — scope the lookup to this store
        restaurantId,
        username: form.username,
        pin: form.pin,
        terminalId,
        openingFloat,
        denominations,
      });
      const { token, staff, shift, restaurantName } = res.data;

      // Step 2: on fresh start, create the outer PosShiftSession so the shift
      // survives page refresh. On resume the session already exists.
      if (!isResume) {
        try {
          const { data } = await api.post('/pos/shift-session/open', {
            terminalId,
            openingCash: openingFloat,
          });
          setActiveSession(data.session);
        } catch (sessionErr) {
          // Non-fatal — POS still works without session continuity
          console.warn('PosShiftSession create failed:', sessionErr?.response?.data || sessionErr.message);
        }
      }

      setPosSession(token, staff, shift, restaurantName);
      navigate('/pos/floor');
    } catch (err) {
      if (!restaurantId && err.response?.status === 400 && err.response?.data?.code === 'MISSING_FIELDS') {
        await openStorePicker();
        return;
      }
      setError(err.response?.data?.message || err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-full flex items-center justify-center bg-gray-50 p-6">
      <div className="bg-white rounded-2xl shadow-lg w-full max-w-md p-8">
        <div className="flex flex-col items-center mb-6">
          <UtensilsCrossed className="h-10 w-10 text-blue-600 mb-2" />
          <h1 className="text-2xl font-bold text-gray-900">
            {isResume ? 'Resume Shift' : 'Start Shift'}
          </h1>
          <p className="text-sm text-gray-500">
            {isResume
              ? 'Enter your credentials to re-authorise this terminal'
              : 'Cashier login required to begin'}
          </p>
        </div>

        {isResume && activeSession && (
          <div className="mb-5 bg-indigo-50 border border-indigo-100 rounded-xl px-4 py-3 text-xs space-y-1 text-indigo-800">
            <div className="flex justify-between">
              <span className="text-indigo-500">Session</span>
              <span className="font-semibold">{activeSession.sessionNo}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-indigo-500">Cashier</span>
              <span className="font-semibold">{activeSession.cashierName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-indigo-500">Opened</span>
              <span className="font-semibold">
                {formatDistanceToNow(new Date(activeSession.openedAt), { addSuffix: true })}
              </span>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {error}
          </div>
        )}

        {storeOptions && (
          <div className="space-y-4">
            <div>
              <h2 className="text-base font-semibold text-ink">Select your store</h2>
              <p className="text-sm text-ink-body">
                Choose the store this terminal belongs to. It will be remembered on this device.
              </p>
            </div>
            {storeOptions.length === 0 ? (
              <p className="text-sm text-ink-body">
                No stores are assigned to your account. Ask an administrator to add one.
              </p>
            ) : (
              <div role="radiogroup" aria-label="Store" className="space-y-2">
                {storeOptions.map((s) => (
                  <label
                    key={s._id}
                    className={`flex items-center gap-3 border rounded-lg px-3 py-3 text-sm cursor-pointer ${
                      selectedStoreId === s._id ? 'border-primary-500 bg-primary-50 text-ink' : 'border-ink-line text-ink'
                    }`}
                  >
                    <input
                      type="radio"
                      name="store"
                      value={s._id}
                      checked={selectedStoreId === s._id}
                      onChange={() => setSelectedStoreId(s._id)}
                    />
                    <span className="font-medium">{s.name}</span>
                    {s.location && <span className="text-ink-body">{s.location}</span>}
                  </label>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setStoreOptions(null)} disabled={loading}>
                Back
              </Button>
              <Button
                fullWidth
                onClick={handleStoreContinue}
                disabled={!selectedStoreId}
                loading={loading}
              >
                Continue
              </Button>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className={`space-y-4 ${storeOptions ? 'hidden' : ''}`}>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Username</label>
            <input
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              value={form.username}
              onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
              required
              autoFocus
              autoComplete="username"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">PIN</label>
            <input
              type="password"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              value={form.pin}
              onChange={(e) => setForm((f) => ({ ...f, pin: e.target.value }))}
              required
              maxLength={8}
            />
          </div>

          {/* Terminal ID — read-only when resuming, editable on fresh start */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Terminal ID</label>
            {isResume ? (
              <input
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-500 cursor-not-allowed"
                value={activeSession.terminalId}
                readOnly
              />
            ) : (
              <>
                <input
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  value={form.terminalId}
                  onChange={(e) => setForm((f) => ({ ...f, terminalId: e.target.value }))}
                  placeholder="POS-01"
                  required
                />
                <p className="text-xs text-gray-400 mt-1">
                  Identifies this cashier station. Saved for future sessions.
                </p>
              </>
            )}
          </div>

          {/* Denomination grid — only on fresh start */}
          {!isResume && (
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-2">
                Opening Float — Total: ₱{(openingFloat / 100).toFixed(2)}
              </label>
              <div className="grid grid-cols-3 gap-2">
                {DENOMINATIONS.map((d) => (
                  <div key={d} className="flex items-center gap-1">
                    <span className="text-xs text-gray-600 w-8 text-right">₱{d}</span>
                    <input
                      type="number"
                      min="0"
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm text-center"
                      value={denoms[d]}
                      onChange={(e) =>
                        setDenoms((d2) => ({
                          ...d2,
                          [d]: Math.max(0, parseInt(e.target.value) || 0),
                        }))
                      }
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {loading ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Lock className="h-4 w-4" />
            )}
            {loading
              ? isResume ? 'Resuming…' : 'Starting…'
              : isResume ? 'Resume Shift' : 'Start Shift'}
          </button>
        </form>
      </div>
    </div>
  );
}
